import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NeuralCanvas from '../../components/NeuralCanvas';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import './Terms.css';

// Componente para el skeleton loader
const SkeletonLoader = () => (
  <div className="skeleton-container">
    <div className="skeleton-header"></div>
    <div className="skeleton-date"></div>
    
    <div className="skeleton-section">
      <div className="skeleton-title"></div>
      <div className="skeleton-text"></div>
      <div className="skeleton-text"></div>
    </div>
    
    <div className="skeleton-section">
      <div className="skeleton-title"></div>
      <div className="skeleton-text"></div>
      <div className="skeleton-text"></div>
    </div>
    
    <div className="skeleton-section">
      <div className="skeleton-title"></div>
      <div className="skeleton-text"></div>
      <div className="skeleton-text"></div>
    </div>
    
    <div className="skeleton-button"></div>
  </div>
);

const Terms = () => {
  const { t, ready } = useTranslation();
  const navigate = useNavigate();

  // Si las traducciones no están listas, mostramos el skeleton
  if (!ready) {
    return (
      <div className="page-wrapper">
        <div className="overlay"></div>
        <NeuralCanvas />
        <div className="terms-container">
          <SkeletonLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="overlay"></div>
      <NeuralCanvas />
      
      {/* Selector de idioma */}
      <div className="language-selector-container">
        <LanguageSelector />
      </div>
      
      <div className="terms-container">
        <div className="terms-header">
          <h1>{t('terms.title', 'Términos y Condiciones')}</h1>
        </div>
        <div className="terms-content">
          
          <section>
            <h2>{t('terms.introduction.title', 'Introducción')}</h2>
            <p>{t('terms.introduction.content', 'Bienvenido a Floomind. Estos Términos y Condiciones rigen tu uso de nuestro sitio web y servicios. Al acceder o utilizar Floomind, aceptas estos términos en su totalidad. Si no estás de acuerdo con estos términos, por favor no utilices nuestro sitio web o servicios.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.services.title', 'Servicios Ofrecidos')}</h2>
            <p>{t('terms.services.content', 'Floomind es un proyecto de investigación y desarrollo tecnológico que ofrece herramientas para mejorar el bienestar en las personas y su eficiencia cognitiva. Nuestros servicios están en constante evolución y podrían cambiar con el tiempo.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.account.title', 'Cuentas de Usuario')}</h2>
            <p>{t('terms.account.content', 'Para acceder a ciertas funcionalidades de nuestro servicio, podrías necesitar crear una cuenta. Eres responsable de mantener la confidencialidad de tu información de acceso y de todas las actividades que ocurran bajo tu cuenta.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.intellectual.title', 'Propiedad Intelectual')}</h2>
            <p>{t('terms.intellectual.content', 'Todo el contenido de Floomind, incluyendo textos, gráficos, logotipos, iconos, imágenes, clips de audio, descargas digitales y software, es propiedad de Floomind o sus proveedores de contenido y está protegido por leyes internacionales de propiedad intelectual.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.user.title', 'Contenido del Usuario')}</h2>
            <p>{t('terms.user.content', 'Al enviar, publicar o mostrar contenido en Floomind, nos otorgas una licencia mundial, no exclusiva, libre de regalías para usar, reproducir, adaptar, publicar, traducir y distribuir tu contenido en cualquier medio. Esta licencia es con el único propósito de mejorar nuestros servicios.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.conduct.title', 'Normas de Conducta')}</h2>
            <p>{t('terms.conduct.content', 'Al utilizar Floomind, aceptas no participar en actividades que puedan dañar, deshabilitar, sobrecargar o deteriorar el servicio. No debes intentar acceder a áreas o información a las que no tienes permiso de acceso.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.liability.title', 'Limitación de Responsabilidad')}</h2>
            <p>{t('terms.liability.content', 'Floomind y sus colaboradores no serán responsables por daños directos, indirectos, incidentales, especiales, consecuentes o punitivos, incluyendo, entre otros, pérdida de beneficios, datos o uso, derivados de tu uso o incapacidad para usar nuestros servicios.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.changes.title', 'Cambios en los Términos')}</h2>
            <p>{t('terms.changes.content', 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios entrarán en vigor inmediatamente después de su publicación en nuestro sitio web. Tu uso continuado de Floomind después de tales cambios constituye tu aceptación de los nuevos términos.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.termination.title', 'Suspensión y Cancelación del Acceso')}</h2>
            <p>{t('terms.termination.content', 'Podemos terminar o suspender tu acceso a Floomind inmediatamente, sin previo aviso o responsabilidad, por cualquier razón, incluyendo sin limitación si incumples estos Términos y Condiciones.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.law.title', 'Ley Aplicable')}</h2>
            <p>{t('terms.law.content', 'Estos términos se regirán e interpretarán de acuerdo con las leyes internacionales, sin considerar sus disposiciones sobre conflictos de leyes.')}</p>
          </section>
          
          <section>
            <h2>{t('terms.contact.title', 'Contacto')}</h2>
            <p>{t('terms.contact.content', 'Si tienes alguna pregunta sobre estos Términos y Condiciones, por favor contáctanos en:')}</p>
            <p><strong>Email:</strong> hello@floomind.com</p>
          </section>
          
          <p className="effective-date">{t('terms.effectiveDate', 'Fecha de entrada en vigor: 1 de Junio de 2025')}</p>
        </div>
      </div>
      
      {/* Botón para volver a la página principal */}
      <div className="button-container">
        <a href="/" className="back-button">
          {t('common.home')}
        </a>
      </div>
    </div>
  );
};

export default Terms; 